import { zodResolver } from '@hookform/resolvers/zod'
import { t } from 'i18next'
import { useForm, FormProvider } from 'react-hook-form'

import { stepsFlow } from '@/features/proposta/entities/Comprador/steps'
import { RepresentanteLegalUseCase } from '@/features/proposta/entities/Comprador/use-case/representante-legal-use-case'
import type { TClienteCompleto } from '@/features/proposta/hooks/use-orquestrar-criacao-edicao'
import { useStepManager } from '@/features/proposta/hooks/use-stepper-proposta'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { CompradorPessoaFisicaStepSchemaOutput } from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import type {
  CompradorPessoaJuridicaSchemaInput,
  CompradorPessoaJuridicaSchemaOutput,
} from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import { StepCompradorLayout } from '@/pages/proposta/etapa-comprador/cadastro-comprador/components/stepper-comprador'
import { ContatosComprador } from '@/pages/proposta/etapa-comprador/cadastro-comprador/contatos-comprador'
import { DadosCompradorPJ } from '@/pages/proposta/etapa-comprador/cadastro-comprador/dados-comprador-pj'
import { DadosProcuradorEmpresa } from '@/pages/proposta/etapa-comprador/cadastro-comprador/dados-procurador'
import { DadosRepresentanteLegal } from '@/pages/proposta/etapa-comprador/cadastro-comprador/dados-representante-legal'
import { EstadoCivilVinculo } from '@/pages/proposta/etapa-comprador/cadastro-comprador/estado-civil-vinculo'
import { TipoResponsavelPJ } from '@/pages/proposta/etapa-comprador/cadastro-comprador/tipo-responsavel-pj'
import type { TNacionalidade, TProfissao } from '@/types/cliente'

type TCadastroCompradorPJProps = {
  mode: 'create' | 'edit'
  onCancel: () => void
  onSubmit: (data: CompradorPessoaJuridicaSchemaOutput, isDirty: boolean) => Promise<void>
  nacionalidades: TNacionalidade[]
  profissoes: TProfissao[]
  compradores: TCompradoresCadastrados
  clienteJuridicoToEdit: TClienteCompleto | null
  emailsJaVinculados: Record<string, string>
}

export function CadastroCompradorPJ({
  mode,
  onCancel,
  onSubmit,
  nacionalidades,
  clienteJuridicoToEdit,
  profissoes,
  compradores,
  emailsJaVinculados,
}: Readonly<TCadastroCompradorPJProps>) {
  const { schema, conditionalSteps } = stepsFlow.compradorPessoaJuridica
  const vinculo = clienteJuridicoToEdit?.vinculo

  const methods = useForm<CompradorPessoaJuridicaSchemaInput, unknown, CompradorPessoaJuridicaSchemaOutput>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      emailsJaVinculados,
      mode: mode,
      tipo: 'PessoaJuridica',
      fluxoIndependente: vinculo ? 'Representante' : null,
      dados: {
        buscouCnpj: mode === 'edit',
        cnpj: clienteJuridicoToEdit?.documento ?? '',
        email: clienteJuridicoToEdit?.email ?? '',
        razaoSocial: clienteJuridicoToEdit?.nome ?? '',
        id: clienteJuridicoToEdit?.id ?? undefined,
        telefone: vinculo
          ? null
          : (clienteJuridicoToEdit?.telefone ?? { origem: 'Brasil', numero: '', principal: true }),
        emailsJaVinculados,
      },
      responsavel: vinculo
        ? {
            emailsJaVinculados,
            imagem: vinculo.imagem ?? null,
            tipoDoCliente: 'Contato',
            tipo: 'Representante',
            dados: {
              buscouCpf: true,
              cpf: vinculo.documento ?? '',
              nome: vinculo.nome ?? '',
              profissaoId: vinculo.profissaoId ?? '',
              nacionalidadeId: vinculo.nacionalidadeId ?? nacionalidades[0]?.id,
              maiorDe18Anos: 'sim',
              email: vinculo.email ?? null,
              id: vinculo.id ?? '',
            },
            estadoCivil: {
              regimeCasamento: vinculo.regimeCasamento ?? null,
              status: vinculo.estadoCivil ?? null,
              tipoAssinatura: vinculo.tipoAssinatura ?? undefined,
              uniaoEstavel: vinculo.uniaoEstavel ?? undefined,
            },
            contato: {
              email: vinculo.email ?? '',
              telefone: {
                numero: vinculo.telefone.numero ?? '',
                origem: vinculo.telefone.origem ?? 'Brasil',
                principal: true,
              },
            },
          }
        : RepresentanteLegalUseCase.iniciarDefaultValues(emailsJaVinculados, nacionalidades[0]?.id),
    },
  })
  const isDirty = methods.formState.isDirty
  const { steps, idx, onNext, onBack, onFinish, isLast } = useStepManager(
    conditionalSteps,
    () => methods.getValues(),
    onCancel,
    onSubmit,
    isDirty,
  )

  const currentStep = steps[idx]
  const handleOnFinish = async (
    output: CompradorPessoaFisicaStepSchemaOutput | CompradorPessoaJuridicaSchemaOutput,
  ) => {
    await onFinish(output as CompradorPessoaJuridicaSchemaOutput)
  }

  const renderContent = () => {
    switch (currentStep) {
      case 'dadosPJ':
        return (
          <DadosCompradorPJ
            compradores={compradores}
            onBack={onBack}
            onNext={onNext}
            nacionalidadePrincipalId={nacionalidades[0]?.id}
            isLast={isLast}
            onFinish={handleOnFinish}
            emailsJaVinculados={emailsJaVinculados}
          />
        )
      case 'tipoResponsavel':
        return <TipoResponsavelPJ onBack={onBack} onNext={onNext} />
      case 'dadosRepresentanteLegal':
        return (
          <DadosRepresentanteLegal
            compradores={compradores}
            nacionalidades={nacionalidades}
            onBack={onBack}
            onNext={onNext}
            profissoes={profissoes}
          />
        )
      case 'dadosProcurador':
        return (
          <DadosProcuradorEmpresa
            compradores={compradores}
            nacionalidades={nacionalidades}
            onBack={onBack}
            onNext={onNext}
            profissoes={profissoes}
            emailsJaVinculados={emailsJaVinculados}
          />
        )
      case 'contatosRepresentanteLegal':
      case 'contatosProcurador':
        return (
          <ContatosComprador
            fieldName="responsavel.contato"
            titulo={t('proposta:cadastro_compradores.steps.contatos.responsavel_titulo', {
              responsavel: currentStep === 'contatosRepresentanteLegal' ? 'Representante Legal' : 'Procurador',
            })}
            onBack={onBack}
            onNext={onNext}
            isLast={isLast}
            onFinish={(e) => handleOnFinish(e as CompradorPessoaJuridicaSchemaOutput)}
          />
        )
      case 'estadoCivilRepresentanteLegal':
      case 'estadoCivilProcurador':
        return (
          <EstadoCivilVinculo
            fieldPath="responsavel.estadoCivil"
            isLast={isLast}
            onNext={onNext}
            onBack={onBack}
            onFinish={(e) => handleOnFinish(e as CompradorPessoaJuridicaSchemaOutput)}
          />
        )

      default:
        return null
    }
  }

  return (
    <FormProvider {...methods}>
      <StepCompradorLayout steps={steps} current={idx} onNext={onNext} onBack={onBack} onCancel={onCancel}>
        {renderContent()}
      </StepCompradorLayout>
    </FormProvider>
  )
}
